/**
 * PS-6495 — the events the Connect page emits to Graylog.
 *
 * Deliberately a short, closed list. Four usage events describe the whole funnel of the guided setup, and
 * a closed union is what keeps a dashboard query stable: a free string would drift the moment
 * someone types `connect.setup.signedIn` next to `connect.setup.signed_in`.
 *
 * **Nothing before the sign-in.** The Connect page is reached from an emailed link by an anonymous
 * visitor, and the log bridge requires a bearer token (`authRequired: true` on
 * `POST /customersupport/v1/logmessage`, verified 2026-10-09). So the earliest point at which this
 * app can log anything is the moment it has a token — which is also the first point at which an
 * event can be attributed to anyone. The two constraints agree, and that is why the catalog starts
 * where it does (Torsten, 2026-10-09).
 */
export const LOG_EVENTS = {
	/**
	 * A customer signed in through the dialog. Carries WHICH case the payload put them in, so the
	 * funnel can be read per case rather than as one number.
	 */
	signedIn: 'connect.setup.signed_in',

	/**
	 * PS-6646 — a sign-in was refused because its phone service is not the callnote's. Carries both
	 * phone service ids, never the username. Sent with the refused account's token, the only bearer
	 * there is at that moment, and before it is discarded.
	 */
	signInWrongAccount: 'connect.setup.sign_in_wrong_account',

	/**
	 * The customer settled the number that will be signalled. Carries where it came from — a row
	 * they picked or a number they typed — and **never the number itself** (Torsten, 2026-10-09).
	 */
	numberSet: 'connect.setup.number_set',

	/**
	 * phoneservice answered the number check, or could not be asked (`outcome: 'unavailable'`, the
	 * run continued on the client pre-check). Carries the outcome, never the number.
	 */
	numberValidated: 'connect.setup.number_validated',

	/** A verification call was asked for. Carries the trigger outcome - never the code. */
	verificationTriggered: 'connect.setup.verification_triggered',

	/** A verification attempt reached its end. Carries the final status and its screen family. */
	verificationFinished: 'connect.setup.verification_finished',

	/** The SIP-Anschluss was booked. Carries what the endpoint answered. */
	ordered: 'connect.setup.order_placed',

	/** The run ended without a booking — closed, cancelled, or failed. */
	ended: 'connect.setup.ended',

	/**
	 * Something went wrong that the customer is not shown the cause of (L17, code review 2): a run
	 * that could not continue, or a booking that came back without a number or with no answer at
	 * all. Logged at ERROR with a diagnostic `reason` - never personal data. Replaces the
	 * `console.error` calls that left the only trace in the customer's own browser.
	 */
	failed: 'connect.setup.failed',
} as const;

export type LogEvent = (typeof LOG_EVENTS)[keyof typeof LOG_EVENTS];
